import React, {FC} from 'react'
import {ID} from '@/_metronic/helpers'
import * as Yup from 'yup'
import {Formik, Field, Form} from 'formik'
import {InputText, FormActions, handleErrors, Checkbox } from '@/components/form'
import {Card} from '@/components/Card'
import {CustomerSettings} from '../../../core/_models'
import {updateSettings} from '../../../core/_requests'
import ProgressBar from '@/components/ProgressBar'

const schema = Yup.object({
  purchaseLimit: Yup.object().shape({
    daily: Yup.number().integer().typeError('limit must be a number').positive().notRequired(),
    monthly: Yup.number().integer().typeError('limit must be a number').positive().notRequired(),
  })
})

type Props = {
  settings: CustomerSettings
  customerId: ID
  refreshCustomer: () => Promise<void>
  maxDevices: number // default global value
  maxIsp: number // default global value
  maxMsisdn: number // default global value
  defaultLimits: {
    daily: number
    monthly: number
  }
  spent: {
    daily: number
    monthly: number
  }
}

const SettingsForm: FC<Props> = ({settings, customerId, refreshCustomer, defaultLimits, spent, maxIsp, maxDevices, maxMsisdn}) => {

  return (
    <Formik
      initialValues={settings}
      validationSchema={schema}
      onSubmit={async (values, {setErrors}) => {
        try {
          await updateSettings(customerId, values)
          await refreshCustomer()
        } catch (e: unknown) {
          handleErrors(e, setErrors)
        }
      }}
    >
      {({values, resetForm }) => (
        <Form id="brand" className='form'>
          <div className="row">
            <div className="col-xl-4">
              <Card title="Purchase limits">
                <Field
                  name="purchaseLimit.daily"
                  placeholder={defaultLimits.daily}
                  type="number"
                  component={InputText}
                  label="Daily"
                  required={false}
                  description={
                    <>
                      <ProgressBar
                        min={0}
                        max={values.purchaseLimit.daily || defaultLimits.daily}
                        value={spent.daily}
                        className={{progress: 'bg-success', bar: 'bg-primary'}}
                        showValue={true}
                        height={20}
                      />

                      Override daily purchase limit in credits, <b>default {defaultLimits.daily}</b>
                    </>
                  }
                />

                <Field
                  name="purchaseLimit.monthly"
                  placeholder={defaultLimits.monthly}
                  type="text"
                  component={InputText}
                  label="Monthly"
                  required={false}
                  description={
                    <>
                      <ProgressBar
                        min={0}
                        max={values.purchaseLimit.monthly || defaultLimits.monthly }
                        value={spent.monthly}
                        className={{progress: 'bg-success', bar: 'bg-primary'}}
                        showValue={true}
                        height={20}
                      />

                    Override monthly purchase limit in credits, <b>default {defaultLimits.monthly}</b>
                    </>
                  }
                />
              </Card>
            </div>

            <div className="col-xl-4">
              <Card title="Support">
                <Field
                  name="disableSupport"
                  component={Checkbox}
                  label="Disable support access"
                  defaultChecked={values.disableSupport}
                />
                <span className="text-muted fs-7">
                  Prevent spamming
                </span>
              </Card>

              <Card title="Two Factors Authentication">
                <Field
                  name="otpEnabled"
                  component={Checkbox}
                  label="Enable"
                  defaultChecked={values.otpEnabled}
                />
              </Card>
            </div>

            <div className="col-xl-4">
              <Card title="Purchase restrictions">
                <Field
                  name="disableRestrictions"
                  component={Checkbox}
                  label="Disable purchase restrictions"
                  defaultChecked={values.disableRestrictions}
                />

                <span className="text-danger fs-7">
                  Disable all purchase restriction, VPN, Risk Score..
                </span>

                <div className="separator separator-dashed my-5"></div>

                <Field
                  name="maxDevices"
                  type="number"
                  placeholder={maxDevices}
                  component={InputText}
                  label="Max authorized devices"
                  description={`Default max devices ${maxDevices}`}
                />

                <Field
                  name="maxIsp"
                  type="number"
                  placeholder={maxIsp}
                  component={InputText}
                  label="Max authorized ISP"
                  description={`Default max ISP ${maxIsp}`}
                />

                <Field
                  name="maxMsisdn"
                  type="number"
                  placeholder={maxMsisdn}
                  component={InputText}
                  label="Max authorized Msisdn"
                  description={`Default max Msisdn ${maxMsisdn}`}
                />
              </Card>
            </div>
          </div>

          <Field
            padding="pt-3"
            component={FormActions}
            position="end"
            close={resetForm}
          />
        </Form>
      )}

    </Formik>
  )
}

export default SettingsForm
